import React from 'react';
import { Chip, Spinner, Tooltip } from 'design-system';
import { useObjectPreview } from 'corp/hooks/smartId/useObjectPreview';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { IUserJson } from '@scale/shared/types/user';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import moment from 'moment';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

const UserPreview: React.FC<{ id: string }> = ({ id }) => {
  const { value: user, loading, error } = useObjectPreview<IUserJson>(id, INamedObjectTypes.User);
  const remoAdminCtx = useRemoAdminCtx();
  const hideLifetimeEarnings = true;
  if (error) return <span>Sorry, there was an error</span>;
  if (loading) return <Spinner />;

  return (
    <div className="user-preview">
      <style jsx>{`
        .user-preview__banner {
          font-weight: bold;
          padding: 10px;
          display: flex;
          align-items: center;
        }

        .user-preview__thing-list > div {
          display: flex;
        }

        .user-preview__thing-list strong {
          color: #666;
          margin-right: 10px;
          min-width: 120px;
          text-align: right;
        }
      `}</style>
      <div className="user-preview__banner">
        <Chip size="small" label={user?.ipCountryCode} />
        <h3 style={{ margin: '0 5px' }}>{remoAdminCtx.hidePII ? id : user?.email}</h3>
      </div>
      <div className="user-preview__body">
        <div className="user-preview__thing-list">
          <div>
            <strong>Lookup page</strong>
            <IsomorphicLink target="_blank" rel="noopener noreferrer" href={`/lookup/${id}`}>
              {id}{' '}
            </IsomorphicLink>
          </div>
          <div>
            <strong>Role</strong>
            {user?.accountRole} {!!user?.banReason && <strong>BANNED</strong>}
          </div>
          {!!user?.createdDate && (
            <div>
              <strong>Joined</strong>
              <Tooltip placement={'top'} title={new Date(user?.createdDate).toLocaleString()}>
                <span>{moment(user?.createdDate).fromNow()}</span>
              </Tooltip>
            </div>
          )}
          {!!user?.lastOnlineDatetime && (
            <div>
              <strong>Online</strong>
              <Tooltip
                placement={'top'}
                title={new Date(user?.lastOnlineDatetime).toLocaleString()}
              >
                <span>{moment(user?.lastOnlineDatetime).fromNow()}</span>
              </Tooltip>
            </div>
          )}
          {!!user?.cachedStats?.estTotalPayoutCents && !hideLifetimeEarnings && (
            <div>
              <strong>Lifetime earnings</strong> $
              {(user?.cachedStats?.estTotalPayoutCents / 100).toFixed(2)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPreview;
